import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { isNullOrUndefined, isValidString } from '@pineapplelab/util';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { from, Observable, throwError } from 'rxjs';
import { BrowserStorage } from '@app/models/browserStorage';
import { ToasterService } from '../toaster/toaster.service';
import { SpinnerService } from '../spinner/spinner.service';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';

/** HTTP methods that mutate data — these trigger the global loading overlay. */
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

@Injectable({
  providedIn: 'root'
})
export class HttpClientService {

  /** Tracks how many mutation requests are in flight simultaneously. */
  private activeRequests = 0;

  constructor(
    private toasterService: ToasterService,
    private spinnerService: SpinnerService,
    private router: Router,
    private angularFireAuth: AngularFireAuth,
  ) { }

  /**
   * Returns the current Firebase ID token, transparently refreshing it when
   * it has expired or is about to expire. The SDK handles renewal using the
   * long-lived refresh token, so this works even after days of inactivity.
   * Returns null when there is no signed-in user (e.g. the login request itself).
   */
  private async getFreshToken(): Promise<string | null> {
    try {
      const user = await this.angularFireAuth.currentUser;
      if (!user) { return null; }
      return await user.getIdToken();
    } catch {
      return null;
    }
  }

  /** Applies the shared error / map / finalize pipeline to a dispatched request. */
  private pipeThrough(
    req: HttpRequest<any>,
    next: HttpHandler,
    isMutation: boolean,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError(err => {
        if (err.status === 401) {
          BrowserStorage.delete('userToken');
          const snack = this.toasterService.showError('Sign in again for security reasons!');
          snack.afterDismissed().toPromise().finally(() => {
            this.router.navigateByUrl('/auth/login');
          });
        }
        return throwError(err);
      }),
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // TODO success response
        }
        return event;
      }),
      finalize(() => {
        if (isMutation) {
          this.activeRequests = Math.max(0, this.activeRequests - 1);
          if (this.activeRequests === 0) {
            this.spinnerService.hide();
          }
        }
      }),
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isMutation = MUTATION_METHODS.has(req.method.toUpperCase()) && req.headers.get('X-Skip-Spinner') !== 'true';

    if (isMutation) {
      this.activeRequests++;
      if (this.activeRequests === 1) {
        this.spinnerService.show();
      }
    }

    // If the caller already set an Authorization header (e.g. one-time-use
    // password-reset flows), forward the request unchanged.
    if (!isNullOrUndefined(req.headers.get('Authorization'))) {
      return this.pipeThrough(req, next, isMutation);
    }

    // Ask the Firebase SDK for the current ID token on every request.
    // The SDK silently renews the 1-hour token via the long-lived refresh token,
    // so the call succeeds even after the browser has been idle for days.
    return from(this.getFreshToken()).pipe(
      switchMap(token => {
        if (!isValidString(token)) {
          // No signed-in user (e.g. the login call) — send without auth header.
          return this.pipeThrough(req, next, isMutation);
        }
        // Keep localStorage in sync so other code (isSigned, etc.) stays correct.
        BrowserStorage.set('userToken', token);
        const authedReq = req.clone({
          headers: req.headers.set('Authorization', token),
        });
        return this.pipeThrough(authedReq, next, isMutation);
      }),
    );
  }

}
